import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useFeature } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { formatDay } from './format';

/** "Inspired by: eaten at …" card on a recipe that was created from a tasting note. */
export function InspiredByTasting({
  recipeId,
  enabled = true,
}: {
  recipeId: string;
  enabled?: boolean;
}) {
  const { t } = useTranslation();
  const feature = useFeature('tasting_notes');
  const origins = useQuery({
    queryKey: queryKeys.tasting.origins(recipeId),
    queryFn: () => api.tastingNotes.byRecipe(recipeId),
    enabled: enabled && feature && Boolean(recipeId),
    retry: false,
    staleTime: 60_000,
  });
  if (!origins.data?.length) return null;
  return (
    <section aria-labelledby="inspired-heading" data-testid="inspired-by">
      <h2 id="inspired-heading" className="mb-2 text-lg font-semibold">
        {t('tasting.inspired.title')}
      </h2>
      <ul className="space-y-2">
        {origins.data.map((o) => (
          <li key={o.noteId}>
            <Link
              to={`/tasting/visits/${o.visitId}`}
              className="flex items-start gap-2 rounded-lg border p-3 text-sm hover:bg-accent/40"
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
              <span>
                {o.placeName
                  ? t('tasting.inspired.eatenAt', { place: o.placeName })
                  : t('tasting.inspired.eatenOut')}
                {o.dishName && ` – ${o.dishName}`}
                <span className="block text-xs text-muted-foreground">
                  {[o.city, formatDay(o.visitedAt)].filter(Boolean).join(' · ')}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
